import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search, X, Command, ArrowRight, CornerDownLeft, Users,
} from 'lucide-react'
import api from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { useSettingsStore } from '../stores/settingsStore'
import { ALL_COMMAND_ACTIONS } from '../lib/commandActions'
import type { CommandActionCategory } from '../lib/commandActions'

type CommandItem = {
  id: string
  label: string
  description?: string
  category: CommandActionCategory | 'Pacientes'
  icon: ComponentType<{ className?: string }>
  onSelect: () => void
  keywords?: string[]
  shortcut?: string[]
  permission?: string | string[]
}

type Props = {
  open: boolean
  onClose: () => void
}

const PATIENT_SEARCH_LIMIT = 6
const RECENT_KEY = 'SmartDental_recent_commands'
const RECENT_MAX = 4

function loadRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function pushRecent(id: string) {
  const current = loadRecent().filter((r) => r !== id)
  current.unshift(id)
  localStorage.setItem(RECENT_KEY, JSON.stringify(current.slice(0, RECENT_MAX)))
}

function fuzzyMatch(haystack: string, needle: string): boolean {
  if (!needle) return true
  const h = haystack.toLowerCase()
  const n = needle.toLowerCase().trim()
  if (h.includes(n)) return true
  let hi = 0
  for (let i = 0; i < n.length; i++) {
    const idx = h.indexOf(n[i], hi)
    if (idx === -1) return false
    hi = idx + 1
  }
  return true
}

export function CommandPalette({ open, onClose }: Props) {
  const navigate = useNavigate()
  const { hasPermission } = useAuthStore()
  const enabledActions = useSettingsStore((s) => s.enabledCommandActions)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIndex(0)
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  const allCommands: CommandItem[] = useMemo(() => {
    const run = (path: string) => () => {
      if (path === '#print') {
        window.print()
      } else {
        navigate(path)
      }
      onClose()
    }

    return ALL_COMMAND_ACTIONS.map((action): CommandItem => ({
      id: action.id,
      label: action.label,
      description: action.description,
      category: action.category,
      icon: action.icon,
      onSelect: run(action.path),
      keywords: action.keywords,
      shortcut: action.shortcut,
      permission: action.permission,
    }))
  }, [navigate, onClose])

  const allowedCommands = useMemo(() => {
    const enabledSet = new Set(enabledActions)
    return allCommands.filter((c) => {
      if (!enabledSet.has(c.id)) return false
      if (!c.permission) return true
      const perms = Array.isArray(c.permission) ? c.permission : [c.permission]
      return perms.some((p) => hasPermission(p))
    })
  }, [allCommands, enabledActions, hasPermission])

  const { data: patients } = useQuery({
    queryKey: ['palette-patients', query],
    queryFn: async () => {
      const { data } = await api.get('/patients', {
        params: { search: query, limit: PATIENT_SEARCH_LIMIT },
      })
      return data as Array<{ id: string; firstName: string; lastName: string; phone: string | null }>
    },
    enabled: open && query.trim().length > 1,
    staleTime: 30_000,
  })

  const patientCommands: CommandItem[] = useMemo(() => {
    if (!patients) return []
    return patients.map((p) => ({
      id: `patient.${p.id}`,
      label: `${p.firstName} ${p.lastName}`,
      description: p.phone ?? 'Sin telefono',
      category: 'Pacientes' as const,
      icon: Users,
      onSelect: () => {
        navigate(`/patients/${p.id}`)
        onClose()
      },
    }))
  }, [patients, navigate, onClose])

  const filtered = useMemo(() => {
    const q = query.trim()
    if (!q) {
      const recent = loadRecent()
      const recentItems = recent
        .map((id) => allowedCommands.find((c) => c.id === id))
        .filter((c): c is CommandItem => !!c)
      const suggestions = allowedCommands.filter((c) => c.category === 'Acciones')
      if (recentItems.length > 0) {
        const recentIds = new Set(recentItems.map((r) => r.id))
        return [
          ...recentItems.map((c) => ({ ...c, category: 'Acciones' as const, description: c.description ?? 'Reciente' })),
          ...suggestions.filter((s) => !recentIds.has(s.id)),
        ]
      }
      return suggestions
    }
    const matches = allowedCommands.filter((c) => {
      const haystack = [c.label, c.description ?? '', ...(c.keywords ?? [])].join(' ')
      return fuzzyMatch(haystack, q)
    })
    return [...matches, ...patientCommands]
  }, [query, allowedCommands, patientCommands])

  const grouped = useMemo(() => {
    const order: CommandItem['category'][] = ['Acciones', 'Pacientes', 'Navegacion']
    const map = new Map<string, CommandItem[]>()
    for (const item of filtered) {
      const arr = map.get(item.category) ?? []
      arr.push(item)
      map.set(item.category, arr)
    }
    return order
      .map((cat) => ({ category: cat, items: map.get(cat) ?? [] }))
      .filter((g) => g.items.length > 0)
  }, [filtered])

  const flatList = useMemo(() => grouped.flatMap((g) => g.items), [grouped])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, filtered.length])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, flatList.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const cmd = flatList[activeIndex]
        if (cmd) {
          pushRecent(cmd.id)
          cmd.onSelect()
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, flatList, activeIndex, onClose])

  useEffect(() => {
    const node = listRef.current?.querySelector<HTMLElement>(`[data-cmd-index="${activeIndex}"]`)
    node?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (!open) return null

  let flatIndex = -1

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 backdrop-blur-sm pt-[12vh] p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="bg-surface-1 border border-border rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Command className="w-4 h-4 text-text-tertiary flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar o ejecutar accion..."
            className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-tertiary outline-none"
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 rounded hover:bg-surface-2 text-text-tertiary"
              aria-label="Limpiar"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onClose}
            className="hidden sm:flex items-center gap-1 text-[10px] font-mono text-text-tertiary bg-surface-2 px-1.5 py-0.5 rounded border border-border-subtle"
          >
            ESC
          </button>
        </div>

        <div ref={listRef} className="max-h-[55vh] overflow-y-auto py-1">
          {flatList.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Search className="w-6 h-6 text-text-tertiary mx-auto mb-2" />
              <p className="text-sm text-text-secondary">Sin resultados para "{query}"</p>
              <p className="text-xs text-text-tertiary mt-1">Prueba con otro termino o pulsa ESC</p>
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.category} className="px-1 py-1">
                <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">
                  {group.category}
                </p>
                {group.items.map((item) => {
                  flatIndex++
                  const isActive = flatIndex === activeIndex
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      data-cmd-index={flatIndex}
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onClick={() => {
                        pushRecent(item.id)
                        item.onSelect()
                      }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        isActive ? 'bg-primary-50 text-primary-700' : 'text-text-primary hover:bg-surface-2'
                      }`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-text-tertiary'}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.label}</p>
                        {item.description && (
                          <p className={`text-xs truncate ${isActive ? 'text-primary-600' : 'text-text-tertiary'}`}>
                            {item.description}
                          </p>
                        )}
                      </div>
                      {item.shortcut && (
                        <div className="flex items-center gap-1">
                          {item.shortcut.map((k) => (
                            <kbd
                              key={k}
                              className="text-[10px] font-mono bg-surface-2 px-1.5 py-0.5 rounded border border-border-subtle text-text-tertiary"
                            >
                              {k}
                            </kbd>
                          ))}
                        </div>
                      )}
                      {isActive && !item.shortcut && (
                        <ArrowRight className="w-3.5 h-3.5 text-primary-500" />
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>

        <div className="px-4 py-2 bg-surface-2 border-t border-border flex items-center justify-between text-[10px] text-text-tertiary">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="font-mono bg-surface-1 px-1.5 py-0.5 rounded border border-border-subtle">↑↓</kbd>
              navegar
            </span>
            <span className="flex items-center gap-1">
              <kbd className="font-mono bg-surface-1 px-1.5 py-0.5 rounded border border-border-subtle">
                <CornerDownLeft className="w-2.5 h-2.5 inline" />
              </kbd>
              seleccionar
            </span>
          </div>
          <span>{flatList.length} resultados</span>
        </div>
      </div>
    </div>
  )
}
